<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
  >
    <template
      v-if="$slots.activator"
      #activator="activatorScope"
    >
      <slot
        name="activator"
        v-bind="activatorScope"
      />
    </template>
    <global-card
      :title="title"
      :heading-color="headingColor"
      :closable="closable"
      @close="close"
    >
      <div class="text-body-large">
        <slot>{{ message }}</slot>
      </div>
      <template #actions>
        <v-btn
          color="dark-grey"
          :text="cancelLabel ?? t('actions.close')"
          @click="close"
        />
        <v-btn
          :color="confirmColor"
          :text="confirmLabel ?? title"
          :loading="submitting"
          @click="confirm"
        />
      </template>
    </global-card>
  </v-dialog>
</template>

<script setup lang="ts">
/**
 * Asks the user to confirm an action, then performs it against the API.
 *
 * The confirm-then-call-then-report shape was repeated in full by a dialog per
 * action. Only the wording, the request and what the parent does afterwards
 * actually differ, so those are the props; everything else is shared here.
 *
 * The dialog opens either from the `activator` slot or through `v-model` when
 * the parent drives it. `confirmed` is emitted only once the request has
 * succeeded, so a parent can reload on it.
 */
import axios from 'axios';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useErrorStore } from '@/stores/errors';

const props = withDefaults(defineProps<{
  /** Heading of the dialog, and the confirm button's label unless one is given. */
  title: string;
  /** Text shown in the body. Ignored when the default slot is used. */
  message?: string;
  /** Label of the confirm button, when it should differ from the title. */
  confirmLabel?: string;
  /** Label of the dismiss button, when it should differ from the usual Close. */
  cancelLabel?: string;
  /** Colour of the confirm button. */
  confirmColor?: string;
  /** Colour of the dialog heading, e.g. `error` for destructive actions. */
  headingColor?: string;
  /** Whether the heading shows a close control. */
  closable?: boolean;
  /** HTTP method the action is performed with. */
  method?: 'put' | 'post' | 'patch' | 'delete';
  /** Endpoint the action is performed against. */
  url: string;
  /** Body sent with the request, if any. */
  payload?: Record<string, unknown>;
  /** Query string parameters sent with the request, if any. */
  params?: Record<string, unknown>;
  /** Shown as a success snack when set; nothing is shown when it is not. */
  successMessage?: string;
  /** Shown when the request fails. */
  errorMessage: string;
}>(), {
  method: 'put',
  confirmColor: 'primary',
});

const emit = defineEmits<{
  confirmed: [];
}>();

const dialog = defineModel<boolean>({ default: false });

const { t } = useI18n();
const { showSnackMessage, handleError } = useErrorStore();

const submitting = ref(false);

function close() {
  dialog.value = false;
}

async function confirm() {
  submitting.value = true;

  try {
    // `delete` carries a body under `data`, every other method sends it directly.
    const config = props.params
      ? { params: props.params }
      : undefined;

    await (props.method === 'delete'
      ? axios.delete(props.url, {
          ...config,
          ...(props.payload
            ? { data: props.payload }
            : {}),
        })
      : axios[props.method](props.url, props.payload, config));

    close();

    if (props.successMessage) {
      showSnackMessage(props.successMessage, 'success');
    }

    emit('confirmed');
  }
  catch (error) {
    handleError(error, props.errorMessage);
  }
  finally {
    submitting.value = false;
  }
}
</script>
